/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { DropdownModule } from 'primeng/dropdown';
import { PanelModule } from 'primeng/panel';
import { COMMON_IMPORTS } from '../../common-imports';
import { AxialLengthType, EyeSideValues, GraphDataEthnicity } from '../../backend-response-models/calc-enums.helper';
import {
  AxialCalcResponse,
  AxialPercentileResult,
  CalcTreatmentResponse,
  PercentileSeries,
} from '../../backend-response-models/calc-interfaces.helper';
import { TranslatePipe } from '../../languages/translate.pipe';
import {
  blackColorTL,
  greenColorTL,
  greyColorTL,
  orangeColorTL,
  redColorTL,
  transparentTL,
  whiteColorTL,
  yellowColorTL,
} from '../../helpers/ui.helper';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart, registerables } from 'chart.js';
import { EyeSideDropDown } from '../../shared/shared-enums/enum-helper';
import { getNumberSuffix } from '../../shared/misc';

Chart.register(...registerables, annotationPlugin);

@Component({
  selector: 'app-axial-graph',
  standalone: true,
  imports: [...COMMON_IMPORTS, PanelModule, DropdownModule, ChartModule],
  templateUrl: './axial-graph.component.html',
  styleUrl: './axial-graph.component.css',
})
export class AxialGraphComponent implements OnInit, OnChanges {
  @Input({ required: true })
  axialCalcResponse!: AxialCalcResponse;

  @Input({ required: true })
  treatmentCalcResponse!: CalcTreatmentResponse;

  @Input()
  ethnicity?: GraphDataEthnicity;

  @Input({ required: true })
  mostMyopicEyeSide!: EyeSideValues;

  @Output()
  ethnicityChanged = new EventEmitter<string>();

  public colorDict: Record<number, string> = {
    [99]: redColorTL,
    [97]: redColorTL,
    [95]: redColorTL,
    [90]: orangeColorTL,
    [75]: orangeColorTL,
    [50]: greenColorTL,
    [25]: greenColorTL,
    [10]: greenColorTL,
    [5]: greenColorTL,
    [3]: greenColorTL,
    [1]: greenColorTL,
  };

  public ethnicityDict: Record<GraphDataEthnicity, string> = {
    [GraphDataEthnicity.WESTERN]: 'WESTERN_DATA_CHOICE',
    [GraphDataEthnicity.EAST_ASIAN]: 'EAST_ASIAN_DATA_CHOICE',
  };

  public eyeSideDropDownChoices: EyeSideDropDown[] = [];
  public eyeSideSelected?: EyeSideDropDown;

  public xAxisTitle = 'Age (Years)';
  public yAxisTitle = 'Axial Length (mm)';
  public cycloplegicName = 'Cycloplegic';
  public nonCycloplegicName = 'Non-Cycloplegic';
  public percentileName = 'Percentile';
  public xAxisStart = 6;
  public xAxisEnd = 16;
  public yAxisStart = 20;
  public yAxisEnd = 27;

  data: any;
  options: any;

  constructor(private translatePipe: TranslatePipe) {}

  ngOnInit() {
    if (!this.ethnicity) {
      this.ethnicity = GraphDataEthnicity.WESTERN;
    }

    this.fillTranslations();
    this.setToMyopicEye();
    this.updateGraph();
  }

  private setToMyopicEye() {
    const startingEyeSide = this.eyeSideDropDownChoices.find(e => e.value === this.mostMyopicEyeSide);
    this.eyeSideSelected = startingEyeSide;
  }

  onEyeSideChange(event: any): void {
    console.log('Selected Eye Side:', event.value);
    this.eyeSideSelected = event.value;
    this.fillTranslations();
    this.updateGraph();
  }

  fillTranslations() {
    this.xAxisTitle = this.translatePipe.transform('AXIAL_X_AXIS');
    this.yAxisTitle = this.translatePipe.transform('AXIAL_Y_AXIS');
    this.cycloplegicName = this.translatePipe.transform('CYCLOPLEGIC');
    this.nonCycloplegicName = this.translatePipe.transform('NON_CYCLOPLEGIC');
    this.percentileName = this.translatePipe.transform('PERCENTILE');

    const leftEye = this.translatePipe.transform('LEFT_EYE');
    const leftEyeMostMyopic = this.translatePipe.transform('LEFT_EYE_MOST_MYOPIC');
    const rightEye = this.translatePipe.transform('RIGHT_EYE');
    const rightEyeMostMyopic = this.translatePipe.transform('RIGHT_EYE_MOST_MYOPIC');

    this.eyeSideDropDownChoices = [
      {
        label: this.mostMyopicEyeSide == EyeSideValues.LEFT ? leftEyeMostMyopic : leftEye,
        value: EyeSideValues.LEFT,
      },
      {
        label: this.mostMyopicEyeSide == EyeSideValues.RIGHT ? rightEyeMostMyopic : rightEye,
        value: EyeSideValues.RIGHT,
      },
    ];
  }

  updateGraph() {
    //Sort out axis
    this.updateAxes();

    //Sort out percentile data plots and annotiations
    const releventData = this.axialCalcResponse.ethnicities.find(e => e.graphDataEthnicity === this.ethnicity);
    const percentilePlot = this.createPercentilePlotsAndAnnotations(
      this.treatmentCalcResponse,
      releventData,
      this.axialCalcResponse.todaysAge
    );

    //Sort out plotted points for estimate and actual for the eyeside
    const plottedDataSets = this.createVisitPlotsDataSets();

    this.data = {
      datasets: [...plottedDataSets, ...percentilePlot.dataSets],
    };

    this.options = {
      scales: {
        x: {
          title: {
            display: true,
            text: this.xAxisTitle,
          },
          type: 'linear',
          position: 'bottom',
          min: this.xAxisStart,
          max: this.xAxisEnd,
          ticks: {
            stepSize: 1,
          },
        },
        y: {
          title: {
            display: true,
            text: this.yAxisTitle,
          },
          type: 'linear',
          position: 'left',
          min: this.yAxisStart,
          max: this.yAxisEnd,
          ticks: {
            stepSize: 1,
          },
        },
      },
      layout: {
        padding: {
          right: 35, // Increased right padding
          top: 10,
          bottom: 10,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        annotation: {
          clip: false, // Prevent annotations from being clipped
          common: {
            drawTime: 'afterDatasetsDraw',
          },
          annotations: percentilePlot.annotations,
        },
      },
    };
  }

  private createVisitPlotsDataSets() {
    const eyeSide = this.eyeSideSelected?.value ?? EyeSideValues.LEFT;
    const allPointsOnEyeSide = this.axialCalcResponse.axialPlots.filter(point => point.eyeSide === eyeSide);

    const allPointsFormatted = allPointsOnEyeSide.map(point => {
      return { x: point.decimalAge, y: point.axialLength, axialLengthType: point.axialLengthType };
    });

    allPointsFormatted.sort((a, b) => a.x - b.x);

    const purpleColor = '#D60BF4';
    const greenColor = '#0DFFBF';

    const allPointsRealDataSet = {
      data: allPointsFormatted,
      pointRadius: 5,
      segment: {
        borderColor: (ctx: any) => {
          const current = ctx.p0.raw?.axialLengthType;
          const next = ctx.p1.raw?.axialLengthType;
          if (current !== next) {
            return transparentTL; // Make the connecting line transparent
          }
          return current === AxialLengthType.REAL ? greenColor : purpleColor;
        },
      },
      backgroundColor: (ctx: any) => {
        const axialLengthType = ctx?.raw?.axialLengthType;
        if (!axialLengthType) {
          return transparentTL;
        }
        return axialLengthType === AxialLengthType.REAL ? greenColor : purpleColor;
      },
      borderColor: (ctx: any) => {
        const axialLengthType = ctx?.raw?.axialLengthType;
        if (!axialLengthType) {
          return transparentTL;
        }
        return axialLengthType === AxialLengthType.REAL ? greenColor : purpleColor;
      },
      borderWidth: 3,
    };

    const plottedDataSets = [allPointsRealDataSet];
    return plottedDataSets;
  }

  private updateAxes() {
    this.xAxisStart = 6;
    this.xAxisEnd = 16;
    this.yAxisStart = 20;
    this.yAxisEnd = 27;

    if (this.ethnicity !== GraphDataEthnicity.WESTERN) {
      this.yAxisEnd = 28;
    }

    const allPoints = this.axialCalcResponse.axialPlots;
    const under6 = allPoints.some(point => point.decimalAge < 6);

    if (under6) {
      const smallestAge = Math.min(...allPoints.map(point => point.decimalAge));
      const smallestAgeFloor = Math.floor(smallestAge);
      this.xAxisStart = smallestAgeFloor;
    }

    const over16 = allPoints.some(point => point.decimalAge > 16);
    if (over16) {
      const largestAge = Math.max(...allPoints.map(point => point.decimalAge));
      const largestAgeCeil = Math.ceil(largestAge);
      this.xAxisEnd = largestAgeCeil;
    }
  }

  private createPercentilePlotsAndAnnotations(
    treatmentCalcResponse: CalcTreatmentResponse,
    releventData?: AxialPercentileResult,
    todaysAge?: number
  ) {
    const percentileSeries = [...(releventData?.percentileSeries ?? [])];
    //sort percentiles low to high
    if (percentileSeries.length > 0) {
      percentileSeries.sort((a, b) => a.percentile - b.percentile);
      const copyOfBiggestPercentile = percentileSeries[percentileSeries.length - 1];
      percentileSeries.push(copyOfBiggestPercentile!);
    }

    // create annptations
    const annotations: { [key: number]: any } = {};

    // Add the vertical line annotation for current age

    // create datasets
    const dataSets = percentileSeries.map((series, index) => {
      const percentile = series.percentile;
      const lastPoint = series.points[series.points.length - 1];
      const isLastSeries = percentileSeries.length - 1 === index;
      const isFirstSeries = index === 0;
      const isBottomPercentile = percentile === 5 || percentile === 3;

      const annotation = {
        type: 'label',
        xValue: lastPoint.x,
        yValue: lastPoint.y,
        content: [`${percentile}${getNumberSuffix(percentile)}`],
        font: {
          size: 12,
        },
        position: {
          x: 'right',
          y: 'center',
        },
        xAdjust: 20, // Increased x-adjustment
        yAdjust: isBottomPercentile ? 3 : 0, // Centered vertically
        color: this.colorDict[percentile],
        textAlign: 'left',
        padding: {
          x: 5,
          y: 2,
        },
      };
      annotations[percentile] = annotation;

      const data = series.points.map(point => ({
        x: point.x,
        y: point.y,
      }));

      const label = percentile + ' ' + this.translatePipe.transform('PERCENTILE');
      const color = this.colorDict[percentile] ?? greyColorTL;
      const transparentColor = color + '10';

      let fill = index === 0 ? 'origin' : '-1';
      let backgroundColor = transparentColor;

      if (isLastSeries) {
        fill = 'end';
        backgroundColor = color + '05';
      }

      if (isFirstSeries) {
        backgroundColor = color + '05';
      }

      return {
        label,
        data,
        pointRadius: 0,
        backgroundColor: backgroundColor,
        borderColor: color,
        borderWidth: 1,
        fill: fill,
        lineTension: 0.1,
      };
    });

    //Add vertical line for todays age
    if (todaysAge) {
      annotations[-99999] = {
        type: 'line',
        xMin: todaysAge,
        xMax: todaysAge,
        yMin: this.yAxisStart,
        yMax: this.yAxisEnd,
        borderColor: blackColorTL,
        borderWidth: 2,
        borderDash: [5, 5],
        label: {
          content: this.translatePipe.transform('TODAYS_AGE'),
          display: true,
          position: 'end',
          backgroundColor: whiteColorTL,
          color: blackColorTL,
          font: {
            size: 12,
          },
          padding: 4,
        },
      };
    }

    //Add treatment windows - transparent grey boxes
    if (treatmentCalcResponse.anyTreatmentActiveWindows.length > 0) {
      treatmentCalcResponse.anyTreatmentActiveWindows.forEach((window, index) => {
        const annotation = {
          type: 'box',
          xMin: window.start,
          xMax: window.end,
          yMin: this.yAxisStart,
          yMax: this.yAxisEnd,
          backgroundColor: blackColorTL + '15',
          borderColor: redColorTL + '00',
          // Only add label to the first box
          ...(index === 0 && {
            label: {
              content: 'Treatment',
              display: true,
              position: 'end',
              yAdjust: 0,
              xAdjust: 50,
              backgroundColor: whiteColorTL,
              color: blackColorTL + '70',

              font: {
                size: 12,
              },
            },
          }),
        };

        const randInt = Math.floor(Math.random() * 1000000);
        annotations[666666 + randInt] = annotation;
      });
    }

    return { dataSets, annotations };
  }

  ngOnChanges(changes: SimpleChanges) {
    this.fillTranslations();
    this.setToMyopicEye();
    this.updateGraph();
  }
}
